// Local, dependency-free assistant provider. Works immediately with no AWS
// account. It handles navigation intents and answers company questions from the
// bundled knowledge base. This is also the graceful fallback used when the Lex
// provider is not configured or errors out.

import type { AssistantProvider, AssistantReply } from './types'
import { matchPage, PAGES } from './navigation'
import { isGreeting, matchKnowledge, COMPANY_NAME } from './knowledgeBase'

const pagesByPath = Object.fromEntries(PAGES.map((p) => [p.path, p]))

const NAV_TRIGGERS = [
  'go to',
  'take me to',
  'open',
  'show me',
  'show',
  'navigate',
  'visit',
  'bring up',
  'jump to',
  'see the',
  'view',
]

function wantsNavigation(text: string): boolean {
  const q = text.toLowerCase()
  return NAV_TRIGGERS.some((t) => q.includes(t))
}

export class LocalProvider implements AssistantProvider {
  readonly name = 'local' as const

  async send(text: string): Promise<AssistantReply> {
    const clean = text.trim()

    if (!clean) {
      return { source: 'local', text: 'I did not catch that. Could you say it again?' }
    }

    if (isGreeting(clean)) {
      return {
        source: 'local',
        text: `Hi! I'm the ${COMPANY_NAME} assistant. I can explain what we do or take you to any page — try "show me your projects" or "what services do you offer?"`,
      }
    }

    const page = matchPage(clean)

    // Explicit navigation request → navigate + explain.
    if (page && wantsNavigation(clean)) {
      return {
        source: 'local',
        text: `Sure, opening the ${page.label} page. ${page.summary}`,
        action: { type: 'navigate', path: page.path, label: page.label },
      }
    }

    // Company question → answer, and offer the related page as a follow-up.
    const knowledge = matchKnowledge(clean)
    if (knowledge) {
      const related = knowledge.path ? pagesByPath[knowledge.path] ?? null : null
      return {
        source: 'local',
        text: knowledge.answer,
        action: related
          ? { type: 'navigate', path: related.path, label: related.label }
          : undefined,
      }
    }

    // Page matched by topic but no explicit nav verb → offer it.
    if (page) {
      return {
        source: 'local',
        text: `${page.summary} Would you like me to open the ${page.label} page?`,
        action: { type: 'navigate', path: page.path, label: page.label },
      }
    }

    return {
      source: 'local',
      text: `I can help with anything about ${COMPANY_NAME} — our services, projects, team or how to get in touch — and I can open pages for you. What would you like to know?`,
    }
  }
}
